import { useMediaQuery } from '@mui/material';
import { ApexOptions } from 'apexcharts';
import { useTranslation } from 'react-i18next';

import { HubDetailPageApiResponse } from 'api/traps/Hubs/types';
import { MediaBreakpoints } from 'hooks/Screen/useScreenSize';
import { TrapTableType } from 'components/TrapsTables/trapTables';

interface HubTrapsChartOptionsProps {
  data?: HubDetailPageApiResponse;
}

interface TrapLike {
  connection_status?: string;
}

const countByStatus = (arr: TrapLike[] | undefined | null, status: string) =>
  Array.isArray(arr)
    ? arr.filter(item => item.connection_status != null && item.connection_status === status).length
    : 0;

export const HubTrapsChartOptions = ({ data }: HubTrapsChartOptionsProps) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(MediaBreakpoints.mobile);

  const mctConnected = countByStatus(data?.mct, 'success') || 0;
  const mctDisconnected = countByStatus(data?.mct, 'dark') || 0;

  const srbmConnected = countByStatus(data?.srbm, 'success') || 0;
  const srbmDisconnected = countByStatus(data?.srbm, 'dark') || 0;

  const smstConnected = countByStatus(data?.smst, 'success') || 0;
  const smstDisconnected = countByStatus(data?.smst, 'dark') || 0;

  const srstConnected = countByStatus(data?.srst, 'success') || 0;
  const srstDisconnected = countByStatus(data?.srst, 'dark') || 0;

  const series: number[] = [
    mctConnected,
    mctDisconnected,
    srbmConnected,
    srbmDisconnected,
    smstConnected,
    smstDisconnected,
    srstConnected,
    srstDisconnected,
  ];

  const labels: string[] = [
    t('HubDetail.chart.trapConnected', { type: 'MCT' }),
    t('HubDetail.chart.trapDisconnected', { type: 'MCT' }),

    t('HubDetail.chart.trapConnected', { type: 'SRBM' }),
    t('HubDetail.chart.trapDisconnected', { type: 'SRBM' }),

    t('HubDetail.chart.trapConnected', { type: 'SMST' }),
    t('HubDetail.chart.trapDisconnected', { type: 'SMST' }),

    t('HubDetail.chart.trapConnected', { type: 'SRST' }),
    t('HubDetail.chart.trapDisconnected', { type: 'SRST' }),
  ];

  const colors: string[] = ['#6A37D4', '#D7C7F2', '#94C748', '#E4F1CF', '#FFE529', '#FFF8C2', '#0AB39C', '#BDEAE3'];
  const options: ApexOptions = {
    chart: {
      height: 380,
      type: 'donut',
      toolbar: { show: true },
      fontFamily: 'Poppins Regular, sans-serif',
    },
    series: series,
    labels: labels,
    colors: colors,
    legend: {
      show: !isMobile,
      position: 'right',

      formatter: (label, opts) => {
        const idx = opts.seriesIndex;
        const count = series[idx] ?? 0;

        const nameMap: Record<string, string> = {
          MCT: TrapTableType.MultiCatchMouseTraps,
          SRBM: TrapTableType.SmartRodenBaitModule,
          SMST: TrapTableType.SnapTrapMouse,
          SRST: TrapTableType.SnapTrapRat,
        };

        let fullLabel = label;

        for (const [shortName, fullName] of Object.entries(nameMap)) {
          if (fullLabel.includes(shortName)) {
            fullLabel = fullLabel.replace(shortName, fullName);
            break;
          }
        }

        return `${fullLabel}: ${count}`;
      },
      markers: {
        offsetX: -5,
      },
    },
    tooltip: {
      custom: ({ series, seriesIndex, w }) => {
        const label = w.globals.labels[seriesIndex];
        const value = series[seriesIndex];

        return `<div class="custom-tooltip">
            <div style="background: #ffffff; color: #333; padding: 8px 12px; border-radius: 6px; font-size: 12px; border: 2px solid #6A37D4;">
              ${label} ${value} 
            </div>
          </div>`;
      },
    },
    dataLabels: {
      enabled: true,
      dropShadow: {
        enabled: true,
        color: '#111',
        top: 0,
        left: 0,
        blur: 3,
        opacity: 0.7,
      },
    },
    plotOptions: {
      pie: {
        donut: { size: '50%' },
      },
    },
    fill: {
      type: 'gradient',
    },
  };

  return { options };
};
